import { useState, useMemo } from "react";

export default function Table({
  headers,
  rows,
  className = "",
  headerClassName = "",
  rowClassName = "",
  cellClassName = "",
  cellPadding = "default", // 'compact', 'default', 'comfortable', 'spacious'
  sortable = false,
  defaultSortColumn = null, // Index of column to sort by default
  defaultSortDirection = "desc", // 'asc', 'desc', or null
  onSort = null, // Optional callback when sort changes
}) {
  // Padding presets
  const paddingClasses = {
    compact: "py-2 px-3",
    default: "py-3 px-4",
    comfortable: "py-4 px-5",
    spacious: "py-5 px-6",
  };

  const padding = paddingClasses[cellPadding] || paddingClasses.default;

  // Sorting state
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  // Handle header click for sorting
  const handleHeaderClick = (index) => {
    if (!sortable) return;

    let newDirection = "desc"; // Start with desc
    if (sortColumn === index) {
      // Cycle through: desc -> asc -> null
      if (sortDirection === "desc") {
        newDirection = "asc";
      } else if (sortDirection === "asc") {
        newDirection = null;
        setSortColumn(null);
        if (onSort) onSort(null, null);
        return;
      }
    }

    setSortColumn(index);
    setSortDirection(newDirection);
    if (onSort) onSort(index, newDirection);
  };

  // Sort rows based on current sort state
  const sortedRows = useMemo(() => {
    // When external sort handler is provided, skip internal sorting (caller handles it)
    if (onSort) return rows;

    if (!sortable || sortColumn === null || sortDirection === null) {
      return rows;
    }

    return [...rows].sort((a, b) => {
      const aValue =
        a.cells[sortColumn]?.sortValue ?? a.cells[sortColumn]?.content;
      const bValue =
        b.cells[sortColumn]?.sortValue ?? b.cells[sortColumn]?.content;

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Extract numeric values from strings like "75.5%" or "3.5/5"
      const extractNumber = (val) => {
        if (typeof val === "number") return val;
        if (typeof val === "string") {
          // Try to extract first number from string
          const match = val.match(/[-+]?[\d.]+/);
          if (match) return parseFloat(match[0]);
        }
        return val;
      };

      const aNum = extractNumber(aValue);
      const bNum = extractNumber(bValue);

      // If both are numbers, compare numerically
      if (typeof aNum === "number" && typeof bNum === "number") {
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Otherwise compare as strings
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === "asc") {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [rows, sortColumn, sortDirection, sortable, onSort]);

  // Render sort indicator
  const renderSortIndicator = (index) => {
    if (!sortable) return null;

    if (sortColumn === index) {
      if (sortDirection === "asc") {
        return (
          <span className="ml-1 text-brand-primary inline-block w-3">↑</span>
        );
      } else if (sortDirection === "desc") {
        return (
          <span className="ml-1 text-brand-primary inline-block w-3">↓</span>
        );
      }
    }
    return (
      <span className="ml-1 text-text-muted-light dark:text-text-muted opacity-50 inline-block w-3">
        ↕
      </span>
    );
  };

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full">
        <thead>
          <tr
            className={`border-b border-border-light dark:border-border-light/40 ${headerClassName}`}
          >
            {headers.map((header, index) => {
              const isRightAligned = header.className?.includes("text-right");
              const isCenterAligned = header.className?.includes("text-center");

              return (
                <th
                  key={index}
                  onClick={() => handleHeaderClick(index)}
                  className={`${padding} text-text-secondary-light dark:text-text-secondary font-semibold ${
                    header.className || ""
                  } ${
                    sortable
                      ? "cursor-pointer select-none hover:opacity-70 transition group"
                      : ""
                  }`}
                >
                  <span
                    className={`inline-flex items-center ${
                      isRightAligned
                        ? "justify-end"
                        : isCenterAligned
                        ? "justify-center"
                        : "justify-start"
                    } w-full`}
                  >
                    {header.label}
                    {renderSortIndicator(index)}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={row.onClick}
              className={`border-b border-border dark:border-border last:border-b-0 ${
                row.className || rowClassName
              } ${row.onClick ? 'cursor-pointer hover:opacity-70 transition' : ''}`}
            >
              {row.cells.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`${padding} ${cell.className || cellClassName}`}
                >
                  {cell.content}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Specialized table for metric comparisons (3 columns: Metric, Value, vs Average)
export function MetricTable({
  metrics,
  className = "",
  cellPadding = "default",
}) {
  const headers = [
    { label: "Metric", className: "" },
    { label: "Company", className: "text-right" },
    { label: "vs Average", className: "text-right" },
  ];

  const rows = metrics.map((metric) => ({
    cells: [
      {
        content: metric.name,
        className: "text-text-primary-light dark:text-text-primary",
      },
      {
        content: metric.value,
        className:
          "text-right font-semibold text-text-primary-light dark:text-text-primary",
      },
      {
        content: metric.difference,
        className: `text-right font-semibold ${
          metric.differenceColor ||
          "text-text-primary-light dark:text-text-primary"
        }`,
      },
    ],
  }));

  return (
    <Table
      headers={headers}
      rows={rows}
      className={className}
      cellPadding={cellPadding}
    />
  );
}

// Specialized table for rankings/leaderboards
export function RankingTable({
  headers,
  data,
  columns,
  className = "",
  cellPadding = "default",
  sortable = true, // Default to sortable for ranking tables
  defaultSortColumn = null, // Index of column to sort by default (e.g., 3 for adoption rate)
  defaultSortDirection = "desc", // 'asc', 'desc', or null
  onSort = null,
}) {
  const tableHeaders = headers.map((h) => ({
    label: h.label,
    className:
      h.align === "right"
        ? "text-right"
        : h.align === "center"
        ? "text-center"
        : "",
  }));

  const rows = data.map((item, index) => ({
    onClick: item.onClick,
    cells: columns.map((col) => {
      const content =
        typeof col.render === "function"
          ? col.render(item, index)
          : item[col.key];
      return {
        content: content,
        sortValue: col.sortValue
          ? col.sortValue(item, index)
          : typeof item[col.key] === "number"
          ? item[col.key]
          : content,
        className: `${
          col.align === "right"
            ? "text-right"
            : col.align === "center"
            ? "text-center"
            : ""
        } ${col.className || ""}`,
      };
    }),
  }));

  return (
    <Table
      headers={tableHeaders}
      rows={rows}
      className={className}
      cellPadding={cellPadding}
      sortable={sortable}
      defaultSortColumn={defaultSortColumn}
      defaultSortDirection={defaultSortDirection}
      onSort={onSort}
    />
  );
}

// Simple 3-column grid for metrics (non-table layout)
export function MetricGrid({ metrics, className = "", spacing = "default" }) {
  // Spacing presets for rows
  const spacingClasses = {
    compact: "space-y-2",
    default: "space-y-4",
    comfortable: "space-y-5",
    spacious: "space-y-6",
  };

  const rowSpacing = spacingClasses[spacing] || spacingClasses.default;

  return (
    <div className={`${rowSpacing} ${className}`}>
      {/* Header Row */}
      <div className="grid grid-cols-3 gap-4 pb-3 border-b border-border dark:border-border">
        <div className="font-semibold text-sm text-text-secondary-light dark:text-text-secondary">
          Metric
        </div>
        <div className="font-semibold text-sm text-text-secondary-light dark:text-text-secondary text-right">
          Company
        </div>
        <div className="font-semibold text-sm text-text-secondary-light dark:text-text-secondary text-right">
          vs Average
        </div>
      </div>

      {/* Data Rows */}
      {metrics.map((metric, index) => (
        <div key={index} className="grid grid-cols-3 gap-4">
          <div className="text-text-primary-light dark:text-text-primary">
            {metric.name}
          </div>
          <div className="text-right font-semibold text-text-primary-light dark:text-text-primary">
            {metric.value}
          </div>
          <div
            className={`text-right font-semibold ${
              metric.differenceColor ||
              "text-text-muted dark:text-text-secondary-light"
            }`}
          >
            {metric.difference}
          </div>
        </div>
      ))}
    </div>
  );
}
